import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import RequestRow, { AttentionDot } from "../components/RequestRow";
import useLiveRequests from "../components/useLiveRequests";
import {
  FilePlus, Clock, MessageSquareText, CheckCircle2, Package, ArrowRight, CheckCheck,
} from "lucide-react";
import { ATTENTION, isOverdue, waitedFor, byUrgency, RESPONSE_TARGET_MIN } from "../quoteUtils";
import { getUser } from "../auth";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

// what the staff member should do next, per reason
const ACTION = {
  awaiting_reply: { label: "Reply", to: (r) => `/admin/requests/${r._id}` },
  revision_requested: { label: "Revise quote", to: (r) => `/admin/requests/${r._id}/quote` },
  needs_quote: { label: "Create quote", to: (r) => `/admin/requests/${r._id}/quote` },
  draft_not_sent: { label: "Review & send", to: (r) => `/admin/requests/${r._id}/quote` },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const { requests, stats, loading } = useLiveRequests({ withStats: true });
  const s = stats || {};

  const queue = useMemo(() => requests.filter((r) => r.attention).sort(byUrgency), [requests]);
  const overdueCount = queue.filter((r) => isOverdue(r.attention)).length;
  const recent = requests.slice(0, 5);

  const cards = [
    { icon: FilePlus, color: "text-blue-500 bg-blue-50", n: s.newReqs ?? 0, label: "New Requests", sub: "Last 24 hours", subColor: "text-slate-400" },
    { icon: Clock, color: "text-orange-500 bg-orange-50", n: s.waitingQuote ?? 0, label: "Quoted", sub: "Awaiting customer", subColor: "text-orange-500" },
    { icon: MessageSquareText, color: "text-purple-500 bg-purple-50", n: s.customerReply ?? 0, label: "Pending", sub: "Needs a quote", subColor: "text-purple-500" },
    { icon: CheckCircle2, color: "text-green-500 bg-green-50", n: s.approvedToday ?? 0, label: "Approved Today", sub: "Great job!", subColor: "text-green-500" },
    { icon: Package, color: "text-yellow-500 bg-yellow-50", n: s.ordersToday ?? 0, label: "Declined", sub: "Last 24 hours", subColor: "text-yellow-600" },
  ];

  const a = s.attention || {};
  const breakdown = [
    ["awaiting_reply", "waiting for a reply"],
    ["revision_requested", "asked for a revision"],
    ["needs_quote", "need a quote"],
    ["draft_not_sent", "have an unsent draft"],
  ].filter(([k]) => a[k] > 0);

  return (
    <AdminLayout>
      <main className="p-6 space-y-6">
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <h1 className="text-2xl font-bold text-slate-800">{greeting()}, {user?.name?.split(" ")[0] || "there"}! 👋</h1>
          <p className="text-slate-500 mt-1">Here's what's happening with your requests today.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="flex items-start gap-3">
                <span className={`grid place-items-center w-10 h-10 rounded-full ${c.color}`}><c.icon size={18} /></span>
                <div>
                  <div className="text-3xl font-bold text-slate-800 leading-none">{c.n}</div>
                  <div className="text-sm font-semibold text-slate-700 mt-2">{c.label}</div>
                  <div className={`text-xs mt-0.5 ${c.subColor}`}>{c.sub}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Needs attention */}
        <section className={`rounded-xl border p-6 ${queue.length ? (overdueCount ? "bg-red-50/60 border-red-200" : "bg-amber-50/60 border-amber-200") : "bg-white border-gray-100"}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                {queue.length > 0 && <AttentionDot overdue={overdueCount > 0} />}
                Needs attention {queue.length > 0 && <span className="text-slate-500 font-semibold">({s.attentionTotal ?? queue.length})</span>}
              </h2>
              {queue.length > 0 ? (
                <p className="text-sm text-slate-600 mt-1">
                  {breakdown.map(([k, t], i) => (
                    <span key={k}>{i > 0 && (i === breakdown.length - 1 ? " and " : ", ")}<b>{a[k]}</b> {t}</span>
                  ))}
                  .{overdueCount > 0 && <> <b className="text-red-700">{overdueCount} past the {RESPONSE_TARGET_MIN}-minute response time.</b></>}
                </p>
              ) : (
                !loading && <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><CheckCheck size={16} className="text-green-600" /> All caught up. Every customer has a reply and every request has a quote.</p>
              )}
            </div>
            {queue.length > 0 && (
              <button onClick={() => navigate("/admin/requests?attention=1")} className="text-sm font-semibold text-slate-700 flex items-center gap-1 hover:underline">
                See all in Requests <ArrowRight size={16} />
              </button>
            )}
          </div>

          {queue.length > 0 && (
            <ul className="mt-4 divide-y divide-black/5 bg-white rounded-lg border border-black/5">
              {queue.slice(0, 8).map((r) => {
                const overdue = isOverdue(r.attention);
                const act = ACTION[r.attention.reason];
                return (
                  <li key={r._id} className={`flex items-center gap-4 px-4 py-3 border-l-4 ${overdue ? "border-l-red-500" : "border-l-amber-400"}`}>
                    <AttentionDot overdue={overdue} />
                    <button onClick={() => navigate(`/admin/requests/${r._id}`)} className="min-w-0 flex-1 text-left">
                      <div className="text-sm">
                        <b className="text-teal-700">{r.requestId}</b>
                        <span className="text-slate-700"> · {r.customerName || "Customer"}</span>
                        <span className="text-slate-400"> · {r.partRequired || r.notes || "Part request"}</span>
                      </div>
                      <div className={`text-xs mt-0.5 ${overdue ? "text-red-700" : "text-amber-800"}`}>
                        {ATTENTION[r.attention.reason]?.label} · waiting {waitedFor(r.attention.since)}
                        {r.attention.reason === "awaiting_reply" && r.lastStaffReply && <span className="text-slate-400"> · last reply by {r.lastStaffReply.name}</span>}
                      </div>
                    </button>
                    {act && (
                      <button onClick={() => navigate(act.to(r))}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-white ${overdue ? "bg-red-600 hover:bg-red-700" : "bg-slate-800 hover:bg-slate-900"}`}>
                        {act.label}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {queue.length > 8 && <p className="text-xs text-slate-500 mt-2">Showing the 8 longest-waiting. {queue.length - 8} more in Requests.</p>}
        </section>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">Recent Requests</h2>
            <button onClick={() => navigate("/admin/requests")} className="text-teal-600 font-semibold text-sm flex items-center gap-1">
              View all requests <ArrowRight size={16} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase border-b border-gray-100">
                  <th className="py-3 px-4 font-medium">Request ID</th>
                  <th className="py-3 px-4 font-medium">Customer</th>
                  <th className="py-3 px-4 font-medium">Rego/VIN</th>
                  <th className="py-3 px-4 font-medium">Part Required</th>
                  <th className="py-3 px-4 font-medium">Voice/Photo Notes</th>
                  <th className="py-3 px-4 font-medium">Commenting</th>
                  <th className="py-3 px-4 font-medium">Notes</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <RequestRow key={r._id} r={r} onClick={() => navigate(`/admin/requests/${r._id}`)} />
                ))}
                {!loading && recent.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">No requests yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </AdminLayout>
  );
}
